import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Loader2, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Label a customer by company + person, since several people can share a
// company (e.g. two contacts at "Hunters Ridge Roofing").
const labelFor = (u) => {
  const company = (u.company_names && u.company_names[0]) || u.company_name || "";
  const name = u.name || u.email || "";
  if (company && name && company !== name) return `${company} — ${name}`;
  return company || name;
};

// Active accounts first, then alphabetical by label.
const sortUsers = (list) =>
  [...list].sort((a, b) => {
    const aActive = a.is_active !== false;
    const bActive = b.is_active !== false;
    if (aActive !== bActive) return aActive ? -1 : 1;
    return labelFor(a).localeCompare(labelFor(b));
  });

/**
 * Searchable contractor picker that switches the admin's "View As" session.
 *
 * variant "banner" — compact, sits in the orange impersonation banner
 * variant "header" — for an admin who is not impersonating yet
 */
const ContractorSwitcher = ({ variant = "header" }) => {
  const { impersonate, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Fetch lazily on first open — there are hundreds of contractors and most
  // page loads never need the list.
  const handleOpenChange = async (next) => {
    setOpen(next);
    if (!next || loaded || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`, { params: { role: "customer" } });
      setUsers(sortUsers(data || []));
      setLoaded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load contractors");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (target) => {
    if (switching || target.id === user?.id) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await impersonate(target.id);
      // Full reload so every page refetches under the new identity.
      window.location.assign("/customer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch contractor");
      setSwitching(false);
    }
  };

  const isBanner = variant === "banner";
  const triggerLabel = isBanner ? "Switch contractor" : "View as contractor";

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant={isBanner ? "secondary" : "ghost"}
          disabled={switching}
          data-testid="contractor-switcher-trigger"
          className={
            isBanner
              ? "h-7 gap-1"
              : "h-9 gap-2 text-white hover:bg-[#1a3a6a] hover:text-white"
          }
        >
          {switching ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Users size={14} className={isBanner ? "" : "text-[#f4cc1e]"} />
          )}
          <span className={isBanner ? "" : "hidden md:inline"}>{triggerLabel}</span>
          <ChevronsUpDown size={14} className="opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[320px] p-0 bg-[#091c3c] border-[#1a3a6a]"
      >
        <Command className="bg-[#091c3c]">
          <CommandInput
            placeholder="Search contractors..."
            className="text-white"
            data-testid="contractor-switcher-search"
          />
          <CommandList>
            {loading && (
              <div className="flex items-center gap-2 px-3 py-6 text-sm text-white/60">
                <Loader2 size={14} className="animate-spin" />
                Loading contractors...
              </div>
            )}
            {!loading && <CommandEmpty className="py-6 text-sm text-white/60">No contractor found.</CommandEmpty>}
            {!loading && (
              <CommandGroup>
                {users.map((u) => {
                  const label = labelFor(u);
                  const inactive = u.is_active === false;
                  return (
                    <CommandItem
                      key={u.id}
                      value={`${label} ${u.email || ""}`}
                      onSelect={() => handleSelect(u)}
                      className="cursor-pointer text-white aria-selected:bg-[#1a3a6a]"
                    >
                      <Check
                        size={14}
                        className={`mr-2 ${u.id === user?.id ? "opacity-100 text-[#f4cc1e]" : "opacity-0"}`}
                      />
                      <span className="flex-1 truncate">{label}</span>
                      {inactive && <span className="ml-2 text-xs text-white/40">(inactive)</span>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ContractorSwitcher;
